import * as React from "react";
import { useIcon } from "@salt-ds/core";

import "./status-indicator.css";

/**
 * StatusIndicator — the semantic status glyph, replacing `@salt-ds/core`'s behind
 * `status-badge.tsx`.
 *
 * ── THE ICON COMES FROM THE REGISTRY, ON PURPOSE ──────────────────────────────────────────
 *
 * `useIcon()` is the vendor's semantic icon registry, and this component reads it rather than
 * importing four icons directly. That is not indirection for its own sake: the registry is the
 * single place an application re-points "what does 'error' look like", and a component that
 * hard-codes `ErrorSolidIcon` silently opts out of every such override.
 *
 * It resolves by NAME — `status="error"` looks up `ErrorIcon` — so a status with no registered
 * icon renders NOTHING rather than throwing. That is the vendor's behaviour and this port keeps
 * it: a missing glyph should not take down the row it sits in.
 *
 * ── THE COLOUR IS DECORATIVE, THE LABEL IS NOT ────────────────────────────────────────────
 *
 * The class carries `--salt-status-*-foreground-decorative`, which is the point: colour alone
 * never conveys the status. `aria-label` carries the word, so a screen reader hears "error"
 * where a sighted reader sees red.
 */
export type IndicatorStatus = "error" | "warning" | "success" | "info";

export interface StatusIndicatorProps extends Omit<React.SVGAttributes<SVGElement>, "status"> {
  status: IndicatorStatus;
  size?: number;
}

/**
 * The four registry keys, spelled out rather than built by string concatenation.
 *
 * A `capitalize(status) + "Icon"` lookup needs a cast to index the vendor's typed map, and a
 * cast is exactly what would hide a renamed key: the code keeps compiling and the icon quietly
 * stops rendering. Naming them makes the dependency checkable at build time.
 */
const ICON_KEY = {
  error: "ErrorIcon",
  warning: "WarningIcon",
  success: "SuccessIcon",
  info: "InfoIcon",
} as const satisfies Record<IndicatorStatus, string>;

export const StatusIndicator = React.forwardRef<SVGSVGElement, StatusIndicatorProps>(
  function StatusIndicator({ className, size = 1, status, ...rest }, ref) {
    const IconComponent = useIcon()[ICON_KEY[status]];
    if (IconComponent === undefined) return null;

    const classes = ["saltStatusIndicator", `saltStatusIndicator-${status}`];
    if (className) classes.push(className);

    return (
      <IconComponent
        aria-label={status}
        className={classes.join(" ")}
        size={size}
        {...rest}
        ref={ref}
      />
    );
  },
);
